import { FeatureBox3D } from 'features/assetViz/objects/features/procedural/FeatureBox3D'
import { FeatureLabel3D } from 'features/assetViz/objects/features/procedural/FeatureLabel3D'
import { SurfaceFeature3D } from 'features/assetViz/objects/features/SurfaceFeature3D'
import { useSurfaceProps } from 'features/assetViz/objects/surfaces/util/SurfaceProvider'
import React from 'react'
import {
  GeckoComponentCoords,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoPipeIntegralSupport,
  GeckoPipeNonIntegralSupport,
} from 'types'

/**
 * A component representing the non-integral support on the surface of a pipe
 * section component
 * @param model             The non-integral pipe support model (from the asset model)
 * @param pipeRadius        The radius of the pipe this support is on
 * @constructor
 */
export const PipeSupportFeature3D: React.FC<{
  model: GeckoPipeNonIntegralSupport | GeckoPipeIntegralSupport
  pipeRadius: GeckoMeasurement
}> = ({ model, pipeRadius }) => {
  //----------------------------------------------------------------------------

  const { bounds } = useSurfaceProps()

  const defaultWidth = React.useMemo(
    () => GeckoMeasurement.CreateFromValue(0.1, GeckoMeasurementUnit.Meters),
    []
  )
  const defaultHeight = React.useMemo(
    () => GeckoMeasurement.CreateFromValue(0.1, GeckoMeasurementUnit.Meters),
    []
  )

  let height = defaultHeight
  if (model instanceof GeckoPipeIntegralSupport) {
    height = model.height
  }

  const depth = React.useMemo(
    () =>
      GeckoMeasurement.CreateFromValue(
        pipeRadius.num + 0.1,
        GeckoMeasurementUnit.Meters
      ),
    [pipeRadius.num]
  )

  let width = defaultWidth
  if (model instanceof GeckoPipeIntegralSupport) {
    width = model.width
  }

  const { labelCoords, positionLabelBelow } = React.useMemo(() => {
    const positionLabelBelow =
      model.geometry.x.num < bounds.start.x.plus(bounds.end.x).scale(0.5).num

    const x = positionLabelBelow
      ? model.geometry.x.minus(width)
      : model.geometry.x.plus(width)

    const y = model.geometry.y
    const labelCoords = GeckoComponentCoords.CreateFromMeasurements(
      bounds.componentID,
      x,
      y
    )
    return { labelCoords, positionLabelBelow }
  }, [model, bounds, width])

  //----------------------------------------------------------------------------

  return (
    <>
      <FeatureLabel3D
        feature={model}
        coords={labelCoords}
        anchor={positionLabelBelow ? 'top' : 'bottom'}
      />
      <SurfaceFeature3D model={model}>
        <FeatureBox3D
          feature={model}
          position={[0, -pipeRadius.num, 0]}
          width={width.num}
          height={height.num}
          depth={depth.num}
          debugColor={'purple'}
        />
      </SurfaceFeature3D>
    </>
  )
}
